// ═══════════════════════════════════════════════════════════════
// RiskPortal1 — View Registry (single source of truth for screens)
// ---------------------------------------------------------------
// Every portal screen is declared here ONCE, as a descriptor:
//
//   { id, group, title, subtitle, icon, render(ctx), actions?(ctx), comingSoon? }
//
//   id        — the view key. MUST exist in rbac.js PORTAL_VIEWS and be granted
//               to a role (role.views) for that role to see it.
//   group     — sidebar group label (see NAV_GROUP_ORDER for ordering).
//   title     — nav label + header title.
//   subtitle  — nav tooltip / placeholder copy.
//   icon      — ModuleIcon name.
//   render    — (ctx) => ReactNode. The screen body. Omit for a "coming soon"
//               placeholder (or set comingSoon:true explicitly).
//   actions   — (ctx) => ReactNode. Optional header action buttons (right side).
//
// Role-based visibility is NOT decided here — it flows entirely through rbac.js
// (which views a role holds) + canView(). This registry only says "if a view is
// visible, here is how it looks and renders". Adding a screen = one entry here +
// granting its id in rbac.js. Nothing else changes.
//
// This module imports ONLY screen components (never rbac), so there is no import
// cycle: everything role-related is read off the `ctx` the shell builds.
// ═══════════════════════════════════════════════════════════════

import React from 'react';
import OverviewDashboard from './Pages/OverviewDashboard';
import RiskOwnerDashboard from './Pages/RiskOwnerDashboard';
import RiskManagerDashboard from './Pages/RiskManagerDashboard';
import RiskRegister from './Pages/RiskRegister';
import MyRisks from './Pages/MyRisks';
import ComplianceCoverage from './Pages/ComplianceCoverage';
import RbacGuide from './Pages/RbacGuide';
import UserManagement from './Pages/UserManagement';
import Workflow from './Pages/Workflow';
import BusinessUnits from './Pages/BusinessUnits';
import Projects from './Pages/Projects';
import Categories from './Pages/Categories';
import RiskAppetite from './Pages/RiskAppetite';
import KeyRiskIndicators from './Pages/KeyRiskIndicators';
import Scenarios from './Pages/Scenarios';
import TreatmentPlans from './Pages/TreatmentPlans';
import HeatMap from './Pages/HeatMap';
import NovaAi from './Pages/NovaAi';

// Sidebar group order. Groups render top-to-bottom in this sequence; any group
// with no visible items for the current role is dropped by the shell.
export const NAV_GROUP_ORDER = ['Overview', 'Manage', 'Insights', 'Help', 'Administration'];

// The registry. Order within a group is the order items appear in the sidebar.
export const PORTAL_VIEW_LIST = [
    // ── Overview ──────────────────────────────────────────────
    {
        id: 'home', group: 'Overview', title: 'Dashboard', icon: 'dashboard',
        subtitle: 'At-a-glance summary of your security posture',
        // Role-specific home: Risk Owner → focused "My Risk Tasks"; Risk Manager
        // → "Risk Manager Dashboard" (KPIs + heat map); everyone else → the
        // org-wide analytics dashboard.
        render: (ctx) => ctx.isOwnerHome ? (
            <RiskOwnerDashboard
                overdueActions={ctx.dash.overdueActions}
                onOpenRisk={ctx.openRiskDetail}
            />
        ) : ctx.isManagerHome ? (
            <RiskManagerDashboard
                dash={ctx.dash}
                businessUnit={ctx.scope.businessUnit}
                project={ctx.scope.project}
                onBusinessUnit={ctx.scope.setBusinessUnit}
                onProject={ctx.scope.setProject}
                can={ctx.can}
                goTo={ctx.goTo}
                onOpenRisk={ctx.openRiskDetail}
                onCreateRisk={ctx.openCreateRisk}
            />
        ) : (
            <OverviewDashboard
                dash={ctx.dash}
                businessUnit={ctx.scope.businessUnit}
                project={ctx.scope.project}
                onBusinessUnit={ctx.scope.setBusinessUnit}
                onProject={ctx.scope.setProject}
                // can={ctx.can}
                goTo={ctx.goTo}
                onOpenRisk={ctx.openRiskDetail}
                onCreateRisk={ctx.openCreateRisk}
            />
        ),
        // No shell header actions: every home (Owner / Manager / Overview) now
        // renders its own title row with the right actions inline.
    },

    // ── Manage ────────────────────────────────────────────────
    {
        id: 'assets', group: 'Manage', title: 'Assets', icon: 'assets',
        subtitle: 'Every discovered asset across your estate',
        comingSoon: true,
    },
    {
        id: 'my_risks', group: 'Manage', title: 'My Risks', icon: 'risk',
        subtitle: 'Risks where you are the registered owner',
        render: (ctx) => <MyRisks onOpenRisk={ctx.openRiskDetail} />,
    },
    {
        id: 'risk', group: 'Manage', title: 'Risk Register', icon: 'risk',
        subtitle: 'Open risks, severities and treatment status',
        render: (ctx) => (
            <RiskRegister
                categories={ctx.dash.categories}
                businessUnits={ctx.dash.businessUnits}
                projects={ctx.dash.projects}
                role={ctx.role}
                canCreate={ctx.can('can_create')}
                canConfigure={ctx.can('can_configure') || ctx.can('can_create')}
            />
        ),
    },
    {
        id: 'heatmap', group: 'Manage', title: 'Heat Map', icon: 'score',
        subtitle: 'Likelihood × impact risk matrix',
        render: (ctx) => <HeatMap onOpenRisk={ctx.openRiskDetail} />,
    },
    {
        id: 'treatments', group: 'Manage', title: 'Treatment Plans', icon: 'check_circle',
        subtitle: 'All treatment plans across the register',
        render: (ctx) => <TreatmentPlans canApprove={ctx.can('can_approve')} />,
    },
    {
        id: 'my_treatments', group: 'Manage', title: 'My Treatments', icon: 'check_circle',
        subtitle: 'Treatment plans you are responsible for',
        render: (ctx) => <TreatmentPlans mine canApprove={ctx.can('can_approve')} />,
    },
    {
        id: 'compliance', group: 'Manage', title: 'Compliance', icon: 'compliance',
        subtitle: 'Framework coverage and readiness',
        render: () => <ComplianceCoverage />,
    },

    // ── Insights ──────────────────────────────────────────────
    {
        id: 'kris', group: 'Insights', title: 'Key Risk Indicators', icon: 'score',
        subtitle: 'KRIs and threshold breaches',
        // Managers/assessors edit KRI readings + thresholds; others read-only.
        render: (ctx) => <KeyRiskIndicators canManage={ctx.can('can_assess') || ctx.can('can_configure')} />,
    },
    {
        id: 'scenarios', group: 'Insights', title: 'Scenarios', icon: 'layers',
        subtitle: 'Scenario analysis and stress tests',
        render: (ctx) => <Scenarios canManage={ctx.can('can_assess') || ctx.can('can_configure')} />,
    },
    {
        id: 'nova_ai', group: 'Insights', title: 'FORESEE AI', icon: 'nova',
        subtitle: 'AI-assisted risk categorisation and treatment',
        render: (ctx) => <NovaAi onOpenRisk={ctx.openRiskDetail} />,
    },
    {
        id: 'reports', group: 'Insights', title: 'Reports', icon: 'ledger',
        subtitle: 'Exports, snapshots and history',
        comingSoon: true,
    },

    // ── Help ──────────────────────────────────────────────────
    {
        id: 'guide', group: 'Help', title: 'RBAC Guide', icon: 'questions',
        subtitle: 'What your role can see and do in the portal',
        render: () => <RbacGuide />,
    },

    // ── Administration ────────────────────────────────────────
    {
        id: 'users', group: 'Administration', title: 'User Management', icon: 'users',
        subtitle: 'Roles and access for the Risk Registry',
        render: (ctx) => <UserManagement role={ctx.role} />,
    },
    {
        id: 'workflow', group: 'Administration', title: 'Workflow', icon: 'audit',
        subtitle: 'Customise the risk lifecycle, roles and per-org settings',
        render: (ctx) => <Workflow canManage={ctx.can('can_configure')} />,
    },
    {
        id: 'business_units', group: 'Administration', title: 'Business Units', icon: 'building',
        subtitle: 'Org hierarchy and per-BU risk rollups',
        render: (ctx) => <BusinessUnits role={ctx.role} />,
    },
    {
        id: 'projects', group: 'Administration', title: 'Projects', icon: 'layers',
        subtitle: 'Initiatives and programmes for executive reporting',
        render: (ctx) => <Projects role={ctx.role} businessUnits={ctx.dash.businessUnits} />,
    },
    {
        id: 'categories', group: 'Administration', title: 'Categories', icon: 'coverage',
        subtitle: 'Risk taxonomy — categorise risks for reporting',
        render: (ctx) => <Categories canManage={ctx.can('can_configure')} />,
    },
    {
        id: 'appetite', group: 'Administration', title: 'Risk Appetite', icon: 'budget',
        subtitle: 'Tolerance thresholds per category',
        render: (ctx) => <RiskAppetite canManage={ctx.can('can_configure')} />,
    },
];

// Fast id → descriptor lookup.
const VIEW_BY_ID = Object.fromEntries(PORTAL_VIEW_LIST.map((v) => [v.id, v]));

export const getView = (id) => VIEW_BY_ID[id] || null;

// The default landing view (first in the list).
export const DEFAULT_VIEW = PORTAL_VIEW_LIST[0].id;

// Build the sidebar groups for the CURRENT role: keep only views this role may
// open (canView), grouped + ordered per NAV_GROUP_ORDER, dropping empty groups.
// `iconFor(name)` lets the shell attach a ModuleIcon glyph without this module
// depending on the icon component.
export const buildSidebarGroups = (canView, iconFor) =>
    NAV_GROUP_ORDER
        .map((label) => ({
            label,
            items: PORTAL_VIEW_LIST
                .filter((v) => v.group === label && canView(v.id))
                .map((v) => ({
                    id: v.id,
                    title: v.title,
                    subtitle: v.subtitle,
                    svgIcon: v.icon ? iconFor(v.icon) : undefined,
                })),
        }))
        .filter((g) => g.items.length > 0);
